import React, { useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import {
  authenticateUser,
  setUser,
  refreshToken,
} from "../../../store/actions/authActions";
import { getUserDetailsFromCookie } from "../../../utils/getCookies";
import secureApiService from "../../../services/secureApi";
import vindiApiService from "../../../services/vindiApiService";
import { detectCardBrand } from "../../../utils/cardBrand";
import payCred from "../../../assets/img-payCred.png";
import arrowBack from "../../../assets/arrow-back-outline-1-1.svg";
import loadingGif from "../../../assets/loading-button.gif";
import Message from "./Message";

import { validCreditCardNumber } from "../../../utils/validateCard";
import cpfValidator from "../../../utils/validar-cpf-cnpj";
import pplansData from "../../../utils/pplansData";
import { states } from "../../../utils/states";
import TextMaskCEP from "../../../utils/TextMaskCEP";
import TextMaskCardNumber from "../../../utils/TextMaskCardNumber";
import TextMaskCardCPF from "../../../utils/TextMaskCardCPF";
import TextMaskCardExp from "../../../utils/TextMaskCardExp";
import TextMaskCardCvc from "../../../utils/TextMaskCardCvc";

// V2: Inicializa Iugu SDK para dual tokenization
window.Iugu.setAccountID(process.env.REACT_APP_IUGU_ACCOUNT_ID);

/**
 * IuguV2 - Formulário de pagamento com Dual Tokenization
 *
 * ARQUITETURA V2:
 * - Gera AMBOS os tokens: Vindi (primary) + Iugu (fallback)
 * - Envia ambos (cardToken + token_iugu) para Gateway Orchestrator
 * - Backend escolhe gateway automaticamente (tenta Vindi → fallback Iugu)
 * - Endpoint único: /api/v2/subscriptions
 *
 * @version 2.1.0 - Dual Tokenization
 */
const IuguV2 = (props) => {
  const { selectedpaymentplan: pplan, form, goToStep } = props;
  const user = getUserDetailsFromCookie();

  const initialValue = {
    neighborhood: "",
    street: "",
    streetNumber: "",
    postal_code: "",
    state: "",
    country: "BR",
    city: "",
    documentNumber: "",
    email: "",
    pplan,
    paymentMethod: "credit_card",
    cardToken: "",
    installments: 1,
  };

  const [values, setValues] = useState(initialValue);
  const [cpfNumber, setCpfNumber] = useState("");
  const [cardExp, setCardExp] = useState("");
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [text, setText] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState("");
  const [cpfNumNotValid, setCpfNumNotValid] = useState(false);
  const [cardNumberNotValid, setCardNumberNotValid] = useState(false);
  const [expMonthAndYearNotValid, setExpMonthAndYearNotValid] = useState(false);
  const [installments, setInstallments] = useState(1);
  const pplanData = pplansData[pplan];
  const [cepNotValid, setCepNotValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [price, setPrice] = useState(
    parseFloat(pplanData.totalAnnualPrice.replace(",", ".")),
  );

  const getFieldLabel = (fieldName) => {
    const labels = {
      'card-number': 'Número do cartão',
      'holder-name': 'Nome no cartão',
      'card-exp': 'Data de vencimento',
      'cvv': 'CVC',
      'documentNumber': 'CPF/CNPJ',
      'name': 'Nome completo',
      'postal_code': 'CEP',
      'streetNumber': 'Número',
      'street': 'Endereço',
      'state': 'Estado',
      'city': 'Cidade'
    };
    return labels[fieldName] || fieldName;
  };

  const clearFieldError = (fieldName) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const onChange = (ev) => {
    const { name, value } = ev.target;

    clearFieldError(name);

    if (name === "card-exp") {
      setCardExp(value);
      setExpMonthAndYearNotValid(false);
    }
    if (name === "card-number") {
      const isValidCard = validCreditCardNumber(value);
      setCardNumberNotValid(!isValidCard);
    }
    if (name === "documentNumber") {
      setCpfNumber(value);
      if (
        cpfValidator.validCnpj(value.replace(/(\.|-|\/)/g, "").trim()) ||
        cpfValidator.validCpf(value.replace(/(\.|-|\/)/g, "").trim())
      ) {
        setCpfNumNotValid(false);
      } else {
        setCpfNumNotValid(true);
      }
    }

    setValues({ ...values, [name]: value });
  };

  const fillAddress = (data) => {
    setValues({
      ...values,
      street: data.logradouro,
      state: data.uf,
      city: data.localidade,
      neighborhood: data.bairro,
    });
  };

  const onBlurCep = (ev) => {
    setLoading(true);
    const { value } = ev.target;
    const cep = value?.replace(/[^0-9]/g, "");
    if (cep?.length !== 8) {
      setLoading(false);
      return;
    }

    axios({
      url: `${process.env.REACT_APP_GOLANG_API}/cep/${cep}`,
      method: "GET",
    })
      .then((res) => {
        fillAddress(res.data);
        setLoading(false);
        if (res.data.cep) {
        } else {
          setCepNotValid(true);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const toCent = (amount) => {
    const str = amount.replace(",", ".");
    return str.length < 3 || str[str.length - 3] === "."
      ? Number(str.replace(".", ""))
      : Number(str.replace(".", "")) * 100;
  };

  const validateForm = () => {
    const errors = {};
    const emptyFields = [];
    const invalidFields = [];

    // Validate card number
    const cardNumberElement = document.querySelector('[name="card-number"]');
    const cardNumber = cardNumberElement ? cardNumberElement.value : '';
    if (!cardNumber || cardNumber.trim() === '') {
      emptyFields.push('Número do cartão');
      errors['card-number'] = 'Campo obrigatório';
    } else if (!validCreditCardNumber(cardNumber)) {
      invalidFields.push('Número do cartão');
      errors['card-number'] = 'Número inválido';
    }

    // Validate holder name
    const holderNameElement = document.querySelector('[name="holder-name"]');
    const holderName = holderNameElement ? holderNameElement.value : '';
    if (!holderName || holderName.trim() === '') {
      emptyFields.push('Nome no cartão');
      errors['holder-name'] = 'Campo obrigatório';
    } else if (holderName.trim().length < 2) {
      invalidFields.push('Nome no cartão');
      errors['holder-name'] = 'Nome muito curto';
    }

    // Validate expiration date
    if (!cardExp || cardExp.trim() === '') {
      emptyFields.push('Data de vencimento');
      errors['card-exp'] = 'Campo obrigatório';
    } else {
      const expParts = cardExp.split('/');
      if (expParts.length !== 2 || expParts[0].length !== 2 || expParts[1].length !== 2) {
        invalidFields.push('Data de vencimento');
        errors['card-exp'] = 'Formato inválido (MM/AA)';
      }
    }

    // Validate CVC
    const cvcElement = document.querySelector('[name="cvv"]');
    const cvc = cvcElement ? cvcElement.value : '';
    if (!cvc || cvc.trim() === '') {
      emptyFields.push('Código de segurança (CVC)');
      errors['cvv'] = 'Campo obrigatório';
    } else if (cvc.length < 3) {
      invalidFields.push('Código de segurança (CVC)');
      errors['cvv'] = 'Código inválido';
    }

    // Validate CPF/CNPJ
    if (!cpfNumber || cpfNumber.trim() === '') {
      emptyFields.push('CPF/CNPJ');
      errors['documentNumber'] = 'Campo obrigatório';
    } else if (cpfNumNotValid) {
      invalidFields.push('CPF/CNPJ');
      errors['documentNumber'] = 'Documento inválido';
    }

    // Validate name
    if (!values.name || values.name.trim() === '') {
      emptyFields.push('Nome completo');
      errors['name'] = 'Campo obrigatório';
    } else if (values.name.trim().length < 2) {
      invalidFields.push('Nome completo');
      errors['name'] = 'Nome muito curto';
    }

    // Validate postal code
    if (!values.postal_code || values.postal_code.trim() === '') {
      emptyFields.push('CEP');
      errors['postal_code'] = 'Campo obrigatório';
    } else if (values.postal_code.replace(/\D/g, '').length !== 8) {
      invalidFields.push('CEP');
      errors['postal_code'] = 'CEP inválido';
    }

    // Validate street number
    if (!values.streetNumber || values.streetNumber.trim() === '') {
      emptyFields.push('Número da residência');
      errors['streetNumber'] = 'Campo obrigatório';
    }

    // Validate street
    if (!values.street || values.street.trim() === '') {
      emptyFields.push('Endereço/Rua');
      errors['street'] = 'Campo obrigatório';
    } else if (values.street.trim().length < 5) {
      invalidFields.push('Endereço/Rua');
      errors['street'] = 'Endereço muito curto';
    }

    // Validate state
    if (!values.state || values.state === '') {
      emptyFields.push('Estado');
      errors['state'] = 'Campo obrigatório';
    }

    // Validate city
    if (!values.city || values.city.trim() === '') {
      emptyFields.push('Cidade');
      errors['city'] = 'Campo obrigatório';
    } else if (values.city.trim().length < 2) {
      invalidFields.push('Cidade');
      errors['city'] = 'Nome da cidade muito curto';
    }

    setFieldErrors(errors);

    // Create detailed error message
    let errorMessage = '';
    if (emptyFields.length > 0) {
      errorMessage += `Campos obrigatórios não preenchidos: ${emptyFields.join(', ')}.`;
    }
    if (invalidFields.length > 0) {
      if (errorMessage) errorMessage += ' ';
      errorMessage += `Campos com informações inválidas: ${invalidFields.join(', ')}.`;
    }

    if (errorMessage) {
      errorMessage += ' Por favor, verifique e corrija as informações destacadas.';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errorMessage
    };
  };

  const getErrorMessage = (errors) => {
    const errorMessages = {
      expiration: "Data de validade do cartão inválida. Verifique mês e ano.",
      is_invalid: "Dados do cartão inválidos. Verifique as informações.",
      number: "Número do cartão inválido. Verifique os dígitos.",
      verification_value: "Código de segurança (CVV) inválido.",
      full_name: "Nome do titular do cartão é obrigatório.",
      brand: "Bandeira do cartão não reconhecida.",
    };

    if (typeof errors === "object") {
      for (let field in errors) {
        const errorType = errors[field];
        if (Array.isArray(errorType) && errorType.includes("is_invalid")) {
          return errorMessages[field] || errorMessages["is_invalid"];
        }
        if (errorMessages[field]) {
          return errorMessages[field];
        }
      }
    }

    return "Erro ao processar cartão. Verifique os dados e tente novamente.";
  };

  /**
   * Processa erros da API Iugu e retorna mensagem amigável
   * @param {Object} errorResponse - Resposta de erro da API
   * @returns {string} Mensagem de erro formatada
   */
  const parseIuguError = (errorResponse) => {
    const { data, details, error } = errorResponse || {};

    // Mapeamento de gateway_message da Mastercard/Bandeiras (retornadas pela Vindi)
    const gatewayMessageMap = {
      "Life cycle": "Cartão expirado ou substituído pelo banco emissor. Por favor, utilize um cartão atualizado ou tente via PIX.",
      "Fraud or security": "Transação bloqueada pelo banco por motivos de segurança. Tente com outro cartão ou pague via PIX.",
      "Policy": "Transação não permitida pela política do banco emissor. Tente com outro cartão ou pague via PIX.",
      "Insufficient funds": "Saldo insuficiente. Verifique o limite do cartão ou tente via PIX.",
      "Do not honor": "Transação não autorizada pelo banco emissor. Entre em contato com seu banco ou tente com outro cartão.",
      "Invalid card": "Cartão inválido. Verifique os dados do cartão e tente novamente.",
      "Expired card": "Cartão vencido. Utilize um cartão com data de validade válida.",
      "Lost card": "Cartão reportado como perdido. Entre em contato com seu banco.",
      "Stolen card": "Cartão reportado como roubado. Entre em contato com seu banco.",
      "Security violation": "Violação de segurança detectada. Tente com outro cartão ou pague via PIX.",
      "Transaction not permitted": "Transação não permitida para este cartão. Tente com outro cartão ou pague via PIX.",
    };

    // Mapeamento completo de códigos LR (Learning Return) - Fonte: https://dev.iugu.com/reference/tabela-de-lrs
    const lrCodeMessages = {
      // Códigos numéricos 00-99
      "0": "Transação autorizada com sucesso.",
      "00": "Transação autorizada com sucesso.",
      "01": "Transação não autorizada. Referida (suspeita de fraude) pelo banco emissor.",
      "02": "Transação não autorizada. Referida (suspeita de fraude) pelo banco emissor.",
      "03": "Não foi possível processar a transação. Entre em contato com a Loja Virtual.",
      "04": "Transação não autorizada. Cartão bloqueado pelo banco emissor.",
      "05": "Transação não autorizada. Cartão inadimplente (Do not honor).",
      "06": "Transação não autorizada. Cartão cancelado.",
      "07": "Transação negada. Reter cartão condição especial.",
      "08": "Transação não autorizada. Código de segurança inválido.",
      "09": "Transação cancelada parcialmente com sucesso.",
      "11": "Transação autorizada com sucesso para cartão emitido no exterior.",
      "12": "Transação inválida, erro no cartão.",
      "13": "Transação não permitida. Valor da transação inválido.",
      "14": "Transação não autorizada. Cartão inválido.",
      "15": "Banco emissor indisponível ou inexistente.",
      "19": "Refaça a transação ou tente novamente mais tarde.",
      "21": "Cancelamento não efetuado. Transação não localizada.",
      "22": "Parcelamento inválido. Número de parcelas inválidas.",
      "23": "Transação não autorizada. Valor da prestação inválido.",
      "24": "Quantidade de parcelas inválido.",
      "25": "Pedido de autorização não enviou número do cartão.",
      "28": "Arquivo temporariamente indisponível.",
      "30": "Transação não autorizada. Decline Message.",
      "39": "Transação não autorizada. Erro no banco emissor.",
      "41": "Transação não autorizada. Cartão bloqueado por perda.",
      "43": "Transação não autorizada. Cartão bloqueado por roubo.",
      "46": "Conta encerrada.",
      "51": "Transação não autorizada. Limite excedido/sem saldo.",
      "52": "Cartão com dígito de controle inválido.",
      "53": "Transação não permitida. Cartão poupança inválido.",
      "54": "Transação não autorizada. Cartão vencido.",
      "55": "Transação não autorizada. Senha inválida.",
      "56": "Número do cartão não pertence ao emissor. Número do cartão inválido.",
      "57": "Transação não autorizada. O cartão não foi autorizado.",
      "58": "Transação não permitida. Opção de pagamento inválida.",
      "59": "Transação não autorizada. Suspeita de fraude.",
      "60": "Transação não autorizada.",
      "61": "Banco emissor indisponível.",
      "62": "Transação não autorizada. Cartão restrito para uso doméstico.",
      "63": "Transação não autorizada. Violação de segurança.",
      "64": "Transação não autorizada. Valor abaixo do mínimo exigido pelo banco emissor.",
      "65": "Transação não autorizada. Excedida a quantidade de transações para o cartão.",
      "67": "Transação não autorizada. Cartão bloqueado para compras hoje.",
      "70": "Transação não autorizada. Limite excedido/sem saldo.",
      "72": "Cancelamento não efetuado. Saldo disponível para cancelamento insuficiente.",
      "74": "Transação não autorizada. A senha está vencida.",
      "75": "Senha bloqueada. Excedeu tentativas de cartão.",
      "76": "Cancelamento não efetuado. Banco emissor não localizou a transação original.",
      "77": "Cancelamento não efetuado. Não foi localizado a transação original.",
      "78": "Transação não autorizada. Cartão bloqueado primeiro uso.",
      "79": "Transação não autorizada. Entre em contato com o seu banco.",
      "80": "Transação não autorizada. Divergência na data de transação/pagamento.",
      "81": "Transação não autorizada. A senha está vencida.",
      "82": "Transação não autorizada. Cartão inválido.",
      "83": "Transação não autorizada. Erro no controle de senhas.",
      "85": "Transação não permitida. Falha da operação.",
      "86": "Transação não permitida. Falha da operação.",
      "88": "Falha na criptografia dos dados.",
      "89": "Erro na transação.",
      "90": "Transação não permitida. Falha da operação.",
      "91": "Transação não autorizada. Banco emissor temporariamente indisponível.",
      "92": "Transação não autorizada. Tempo de comunicação excedido.",
      "93": "Transação não autorizada. Violação de regra, possível erro no cadastro.",
      "94": "Transação duplicada.",
      "96": "Falha no processamento.",
      "97": "Valor não permitido para essa transação.",
      "98": "Sistema/comunicação indisponível.",
      "99": "Sistema/comunicação indisponível.",

      // Códigos especiais
      "126": "A data de validade do cartão de crédito é inválida.",
      "475": "Timeout de Cancelamento.",
      "999": "Sistema/comunicação indisponível.",

      // Códigos alfanuméricos
      "AA": "Tempo Excedido.",
      "AB": "Função incorreta (débito).",
      "AC": "Transação não permitida. Cartão de débito sendo usado com crédito. Use a função débito.",
      "AE": "Tente Mais Tarde.",
      "AF": "Transação não permitida. Falha da operação.",
      "AG": "Transação não permitida. Falha da operação.",
      "AH": "Transação não permitida. Cartão de crédito sendo usado com débito. Use a função crédito.",
      "AI": "Transação não autorizada. Autenticação não foi realizada.",
      "AJ": "Transação não permitida. Transação de crédito ou débito em uma operação que permite apenas Private Label. Tente novamente selecionando a opção Private Label.",
      "AV": "Transação não autorizada. Dados Inválidos.",
      "BD": "Transação não permitida. Falha da operação.",
      "BL": "Transação não autorizada. Limite diário excedido.",
      "BM": "Transação não autorizada. Cartão Inválido.",
      "BN": "Transação não autorizada. Cartão ou conta bloqueado.",
      "BO": "Transação não permitida. Falha da operação.",
      "BP": "Transação não autorizada. Conta corrente inexistente.",
      "BP176": "Transação não permitida.",
      "BV": "Transação não autorizada. Cartão vencido.",
      "CF": "Transação não autorizada. Falha na validação dos dados.",
      "CG": "Transação não autorizada. Falha na validação dos dados.",
      "DA": "Transação não autorizada. Falha na validação dos dados.",
      "DF": "Transação não permitida. Falha no cartão ou cartão inválido.",
      "DM": "Transação não autorizada. Limite excedido/sem saldo.",
      "DQ": "Transação não autorizada. Falha na validação dos dados.",
      "DS": "Transação não permitida para o cartão.",
      "EB": "Transação não autorizada. Limite diário excedido.",
      "EE": "Transação não permitida. Valor da parcela inferior ao mínimo permitido.",
      "EK": "Transação não permitida para o cartão.",
      "FA": "Transação não autorizada.",
      "FC": "Transação não autorizada. Ligue Emissor.",
      "FD": "Transação negada. Reter cartão condição especial.",
      "FE": "Transação não autorizada. Divergencia na data de transação/pagamento.",
      "FF": "Cancelamento OK.",
      "FG": "Transação não autorizada. Ligue AmEx 08007285090.",
      "GA": "Aguarde Contato.",
      "GD": "Transação não permitida.",
      "HJ": "Transação não permitida. Código da operação inválido.",
      "IA": "Transação não permitida. Indicador da operação inválido.",
      "JB": "Transação não permitida. Valor da operação inválido.",
      "KA": "Transação não permitida. Falha na validação dos dados.",
      "KB": "Transação não permitida. Selecionado a opção incorrente.",
      "KE": "Transação não autorizada. Falha na validação dos dados.",
      "N3": "Saque não disponível.",
      "N7": "Transação não autorizada. Código de segurança inválido.",
      "N8": "Diferença. Pré autorização.",
      "NR": "Transação não permitida. Retentar a transação após 30 dias.",
      "P5": "Troca de senha / desbloqueio.",
      "R0": "Suspensão de pagamento recorrente para um serviço.",
      "R1": "Transação não autorizada. Cartão inadimplente (Do not honor).",
      "R2": "Transação não qualificada para Visa PIN.",
      "R3": "Suspensão de todas as ordens de autorização.",
      "RE": "Trava por excesso de 'retentativas' de uma transação que já foi negada pelo emissor. Usar outro cartão ou outra forma de pagamento.",
      "RP": "Transação não permitida. Retentar a transação após 72 horas.",
      "RT": "AF GETNET.",
      "U3": "Transação não permitida. Falha na validação dos dados.",

      // Códigos especiais de sistema
      "99A": "Token não encontrado.",
      "99B": "Sistema indisponível/Falha na comunicação.",
      "99C": "Sistema indisponível/Exceção no processamento.",
      "99Z": "Sistema indisponível/Retorno desconhecido.",
      "99TA": "Timeout na requisição. O tempo para receber o retorno da requisição excedeu.",

      // Códigos antifraude
      "AF01": "Recusado manualmente em analise antifraude.",
      "AF02": "Recusado automaticamente em analise antifraude.",
      "AF03": "Recusado pelo antifraude da adquirente de crédito. Transação não permitida conforme análise de acus por suspeita a fraude.",

      // Códigos Amex específicos
      "A2": "Verifique os dados do cartão.",
      "A3": "Erro no cartão. Não tente novamente.",
      "A5": "Transação não permitida. Não tente novamente.",
      "A7": "Erro no cartão. Não tente novamente.",
    };

    try {
      let iuguError = null;

      // Caso 1: details já é um objeto com LR (novo formato)
      if (details && typeof details === 'object' && details.LR) {
        iuguError = details;
      }
      // Caso 2: data é uma string JSON (formato antigo)
      else if (data && typeof data === 'string') {
        const jsonMatch = data.match(/{.*}/s);
        if (jsonMatch) {
          iuguError = JSON.parse(jsonMatch[0]);
        }
      }
      // Caso 3: data já é um objeto
      else if (data && typeof data === 'object') {
        iuguError = data;
      }

      // Se conseguiu extrair iuguError, processar
      if (iuguError) {
        // Verificar código LR
        if (iuguError.LR && lrCodeMessages[iuguError.LR]) {
          return lrCodeMessages[iuguError.LR];
        }

        // Mensagens específicas por status
        if (iuguError.status === "unauthorized") {
          return iuguError.info_message || iuguError.message || "Transação não autorizada pelo banco emissor.";
        }

        // Retornar message ou info_message se disponíveis
        if (iuguError.message) {
          return iuguError.message;
        }

        if (iuguError.info_message) {
          return iuguError.info_message;
        }

        // Verificar errors object
        if (iuguError.errors && Object.keys(iuguError.errors).length > 0) {
          const firstError = Object.values(iuguError.errors)[0];
          if (Array.isArray(firstError)) {
            return firstError.join(", ");
          }
          return firstError;
        }
      }

      // Se tem error ou data como string simples, verificar gateway_message antes
      if (error && typeof error === 'string') {
        // Verificar se contém gateway_message conhecida da Mastercard/Bandeiras
        for (const [key, friendlyMessage] of Object.entries(gatewayMessageMap)) {
          if (error.toLowerCase().includes(key.toLowerCase())) {
            return friendlyMessage;
          }
        }
        return error;
      }

      if (data && typeof data === 'string' && !data.includes('{')) {
        // Verificar gateway_message em data também
        for (const [key, friendlyMessage] of Object.entries(gatewayMessageMap)) {
          if (data.toLowerCase().includes(key.toLowerCase())) {
            return friendlyMessage;
          }
        }
        return data;
      }

      // Fallback
      return "Erro ao processar pagamento. Tente novamente.";
    } catch (parseError) {
      console.error("Erro ao processar resposta da Iugu:", parseError);
      return error || data || "Erro ao processar pagamento. Tente novamente.";
    }
  };

  /**
   * V2: Tokeniza cartão via Vindi (primary gateway)
   * @returns {Promise<string>} Token do cartão Vindi
   */
  const tokenizeViaVindi = async () => {
    try {
      const cardNumber = document.querySelector('[name="card-number"]').value;
      const holderName = document.querySelector('[name="holder-name"]').value;
      const cvc = document.querySelector('[name="cvv"]').value;
      const [expMonth, expYear] = cardExp.split('/').map(v => v.trim());

      // Detectar bandeira automaticamente
      const cardBrand = detectCardBrand(cardNumber.replace(/\s/g, ''));

      const cardData = {
        holder_name: holderName.trim(),
        card_expiration: `${expMonth.padStart(2, '0')}/20${expYear}`,
        card_number: cardNumber.replace(/\s/g, ''),
        card_cvv: cvc,
        payment_method_code: cardBrand,
        payment_company_code: cardBrand,
        allow_as_fallback: true,
      };

      const response = await vindiApiService.tokenizeCard(cardData);
      return response.payment_profile?.gateway_token || response.payment_profile?.id;
    } catch (error) {
      let errorMessage = "Erro ao validar cartão (Vindi): ";

      if (error.type === 'VINDI_API_ERROR') {
        errorMessage += error.message;
      } else if (error.type === 'NETWORK_ERROR') {
        errorMessage = "Erro de conexão. Verifique sua internet e tente novamente.";
      } else {
        errorMessage += error.message || 'Erro desconhecido';
      }

      console.error('[VINDI TOKENIZATION ERROR]', {
        type: error.type,
        message: error.message,
        details: error.details,
      });

      throw new Error(errorMessage);
    }
  };

  /**
   * V2: Tokeniza cartão via Iugu (fallback gateway)
   * @param {HTMLFormElement} formElement - Form element para Iugu
   * @returns {Promise<string>} Token do cartão Iugu
   */
  const tokenizeViaIugu = async (formElement) => {
    return new Promise((resolve, reject) => {
      window.Iugu.createPaymentToken(formElement, (data) => {
        if (data.errors) {
          reject(new Error(getErrorMessage(data.errors)));
        } else {
          resolve(data.id);
        }
      });
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const authURL = `${process.env.REACT_APP_GOLANG_API}/auth/signup`;

    // Validate form before proceeding
    const validation = validateForm();
    if (!validation.isValid) {
      setStatus("error");
      setText(validation.errorMessage);
      return;
    }

    setSubmitDisabled(true);
    setLoading(true);

    // ========================================
    // V2: DUAL TOKENIZATION
    // ========================================
    let cardToken = null;     // Vindi token (primary)
    let token_iugu = null;    // Iugu token (fallback)

    // 1. Tokenize with Vindi (primary)
    try {
      console.log('[V2 TOKENIZATION] Tokenizing with Vindi...');
      cardToken = await tokenizeViaVindi();
      console.log('[V2 TOKENIZATION] ✅ Vindi token created');
    } catch (vindiError) {
      console.warn('[V2 TOKENIZATION] Vindi tokenization failed:', vindiError.message);
      // Continue to Iugu tokenization even if Vindi fails
    }

    // 2. Tokenize with Iugu (fallback)
    try {
      console.log('[V2 TOKENIZATION] Tokenizing with Iugu...');
      token_iugu = await tokenizeViaIugu(event.target);
      console.log('[V2 TOKENIZATION] ✅ Iugu token created');
    } catch (iuguError) {
      console.error('[V2 TOKENIZATION] Iugu tokenization failed:', iuguError.message);
      // If both failed, show error
      if (!cardToken) {
        setLoading(false);
        setSubmitDisabled(false);
        setStatus("error");
        setText("Erro ao validar os dados do cartão. Verifique as informações e tente novamente.");
        return;
      }
    }

    // 3. Build payment payload with BOTH tokens
    let payloadPayment = {
      customer: {
        address: {
          line1: values.street,
          number: values.streetNumber,
          postalCode: values.postal_code,
          state: values.state,
          city: values.city,
        },
        documentNumber: values.documentNumber.replace(/(\.|-|\/)/g, "").trim(),
        email: form.email,
        name: values.name,
      },
      paymentMethod: "credit_card",
      cardToken: cardToken,        // ✅ Vindi token
      token_iugu: token_iugu,      // ✅ Iugu token
      pplan,
      installments: form.discountPercentage === 100 ? 1 : installments,
      planPrice: toCent(pplanData.totalAnnualPrice),
    };

    setLoading(true);

    let authRes, token;
    try {
      authRes = await axios.post(authURL, {
        email: user && user.email ? user.email : form.email,
      });
      setUser(authRes.data.user, false);
      token = authRes.data.token;

      if (form.coupon) {
        console.log("form.coupon", form.coupon);
        payloadPayment = {
          ...payloadPayment,
          coupon: form.coupon.id,
          discount: form.discountPercentage,
        };
      }

      try {
        token = authRes.data.token;

        // ========================================
        // V2: Gateway Orchestrator Endpoint
        // ========================================
        const paymentRes = await secureApiService.customRequest(
          'POST',
          '/subscriptions',  // ✅ Novo endpoint V2 (não mais /iugu/subscriptions)
          payloadPayment,
          { Authorization: `Bearer ${token}` }
        );

        await refreshToken(token);
        if (paymentRes) {
          setLoading(false);
          setSubmitDisabled(false);
          setStatus("success");
          setText("Assinatura realizada com sucesso!");
          setTimeout(() => {
            window.location.href = window.origin + `/minhaconta`;
          }, 1500);
        }
      } catch (err) {
        setLoading(false);
        setSubmitDisabled(false);
        setStatus("error");

        // Usar função parseIuguError para processar erros
        const errorMessage = parseIuguError(err.response?.data);
        setText(errorMessage);
      }
    } catch (err) {
      setLoading(false);
      setStatus("error");

      if (err.response) {
        const errorMessage = parseIuguError(err.response.data);
        setText(errorMessage);
      } else {
        setText("Erro ao processar pagamento. Tente novamente.");
      }
      setSubmitDisabled(false);
    }
  };

  const aplicateDiscount = (value) => {
    let totalPrice = parseFloat(pplanData.totalAnnualPrice.replace(",", "."));
    const priceDiscount =
      totalPrice - (totalPrice * form.discountPercentage) / 100;
    const oldPrice = form.discountPercentage ? priceDiscount : totalPrice;
    return (oldPrice / installments).toFixed(2);
  };

  const handleChange = async (event) => {
    setCpfNumber("");
    setText("");
    const currentInstallments = parseFloat(event.target.value);
    setInstallments(event.target.selectedIndex + 1);
    await setPrice(parseFloat(currentInstallments.toFixed(2)));
  };

  const installmentsOptions = (numberInstallment) => {
    let totalPrice = parseFloat(pplanData.totalAnnualPrice.replace(",", "."));
    const priceDiscount =
      totalPrice - (totalPrice * form.discountPercentage) / 100;
    const oldPrice = form.discountPercentage ? priceDiscount : totalPrice;
    const totalInstallment = [];
    for (let i = 1; i <= numberInstallment; i++) {
      totalInstallment.push(
        <option key={i} value={(oldPrice / i).toFixed(2)}>
          {`${i}x R$${(oldPrice / i).toFixed(2)}`}
        </option>,
      );
    }
    return totalInstallment;
  };

  return (
    <div className="data-creditCard">
      <img
        className="btn-prev"
        src={arrowBack}
        alt="arrow back"
        onClick={() => goToStep(1)}
        style={{ outline: "none" }}
      />
      <img src={payCred} alt="Pagamento" style={{ marginBottom: 20 }} />
      {text && <Message text={text} status={status} />}

      <div className="form-wrapper-scroll">
        <form
          className="card-payment-scroll"
          autoComplete="off"
          id="card-payment"
          onSubmit={handleSubmit}
        >
          {form.discountPercentage !== 100 && (
            <div className="input-group">
              <label className="label-input-group">Parcelas</label>
              <select
                id="parcelas"
                name="parcelas"
                className="parcel-select"
                onChange={handleChange}
              >
                {installmentsOptions(12)}
              </select>
            </div>
          )}

          <div className="input-group padding-input-group">
            <label className="label-input-group">Número no cartão</label>
            <TextMaskCardNumber
              name="card-number"
              className="text-field w-input"
              data-pagarmecheckout-element="number"
              data-iugu="number"
              type="text"
              onChange={onChange}
              style={{
                color: cardNumberNotValid ? "red" : "#212",
                borderColor: fieldErrors['card-number'] ? "red" : "#e0e0e0",
              }}
            />
          </div>

          <div className="input-group padding-input-group">
            <label className="label-input-group">Nome no cartão</label>
            <input
              type="text"
              onChange={onChange}
              name="holder-name"
              className="text-field w-input"
              data-pagarmecheckout-element="number"
              data-iugu="full_name"
              style={{
                borderColor: fieldErrors['holder-name'] ? "red" : "#e0e0e0",
              }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr",
              columnGap: "8px",
            }}
          >
            <div className="input-group padding-input-group">
              <label className="label-input-group">Mês de vencimento</label>
              <TextMaskCardExp
                name="card-exp"
                className="text-field w-input"
                type="text"
                data-iugu="expiration"
                value={cardExp}
                onChange={onChange}
                style={{
                  color: expMonthAndYearNotValid ? "red" : "#212",
                  borderColor: fieldErrors['card-exp'] ? "red" : "#e0e0e0",
                }}
              />
            </div>
            <div className="input-group padding-input-group">
              <label className="label-input-group">CVC</label>
              <TextMaskCardCvc
                name="cvv"
                data-iugu="verification_value"
                data-pagarmecheckout-element="cvv"
                className="text-field w-input"
                type="text"
                onChange={onChange}
                style={{
                  borderColor: fieldErrors['cvv'] ? "red" : "#e0e0e0",
                }}
              />
            </div>
          </div>

          <div className="input-group padding-input-group">
            <label htmlFor="documentNumber" className="label-input-group">
              CPF (ou CNPJ)
            </label>
            <TextMaskCardCPF
              id="documentNumber"
              name="documentNumber"
              type="text"
              className="text-field w-input"
              style={{
                color: cpfNumNotValid ? "red" : "#212",
                borderColor: fieldErrors['documentNumber'] ? "red" : "#e0e0e0",
              }}
              placeholder="Número do documento"
              value={cpfNumber}
              onChange={installments > 1 ? onChange : onChange}
              data-name="Email 3"
            />
          </div>

          {
            <>
              <div className="input-group padding-input-group">
                <label className="label-input-group">Nome completo</label>
                <input
                  type="text"
                  name="name"
                  onChange={onChange}
                  placeholder="Nome e sobrenome"
                  className="text-field w-input"
                  style={{
                    borderColor: fieldErrors['name'] ? "red" : "#e0e0e0",
                  }}
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: "8px",
                }}
              >
                <div className="input-group padding-input-group">
                  <label className="label-input-group">Código postal</label>
                  <TextMaskCEP
                    name="postal_code"
                    className="text-field w-input"
                    type="text"
                    onBlur={(ev) => onBlurCep(ev)}
                    onChange={onChange}
                    style={{
                      color: cepNotValid ? "red" : "#212",
                      borderColor: fieldErrors['postal_code'] ? "red" : "#e0e0e0",
                    }}
                  />
                </div>
                <div className="input-group padding-input-group">
                  <label className="label-input-group">Número</label>
                  <input
                    type="text"
                    name="streetNumber"
                    onChange={onChange}
                    placeholder="N° da rua"
                    className="text-field w-input"
                    style={{
                      borderColor: fieldErrors['streetNumber'] ? "red" : "#e0e0e0",
                    }}
                  />
                </div>
              </div>
              <div className="input-group padding-input-group">
                <label className="label-input-group">Endereço</label>
                <input
                  type="text"
                  name="street"
                  value={values.street}
                  onChange={onChange}
                  placeholder="Nome da rua"
                  className="text-field w-input"
                  style={{
                    borderColor: fieldErrors['street'] ? "red" : "#e0e0e0",
                  }}
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: "8px",
                }}
              >
                <div className="input-group padding-input-group">
                  <label className="label-input-group">Estado</label>
                  <select
                    name="state"
                    onChange={onChange}
                    value={values.state}
                    className="parcel-select"
                    style={{
                      borderColor: fieldErrors['state'] ? "red" : "#e0e0e0",
                    }}
                  >
                    <option value="" disabled selected hidden></option>
                    {states.map((state, index) => (
                      <option key={index} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="input-group padding-input-group">
                  <label className="label-input-group">Cidade</label>
                  <input
                    type="text"
                    name="city"
                    value={values.city}
                    onChange={onChange}
                    className="text-field w-input"
                    style={{
                      borderColor: fieldErrors['city'] ? "red" : "#e0e0e0",
                    }}
                  />
                </div>
              </div>
            </>
          }

          <div id="field_errors" style={{ color: "red" }}>
            {Object.keys(fieldErrors).length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <h6 style={{ color: '#842029', margin: '0 0 8px 0', fontSize: '14px' }}>
                  Corrija os seguintes campos:
                </h6>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px' }}>
                  {Object.entries(fieldErrors).map(([field, error]) => (
                    <li key={field} style={{ color: '#842029', marginBottom: '4px' }}>
                      <strong>{getFieldLabel(field)}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <button
              className="btn btn-primary btn-block cardPayment"
              type="submit"
              disabled={loading || submitDisabled}
            >
              {loading ? (
                <img style={{ width: "20px" }} src={loadingGif} alt="loading" />
              ) : form.discountPercentage === 100 ? (
                "Finalizar Assinatura Gratuita"
              ) : (
                `Pagar ${installments}x R$ ${
                  form.discountPercentage
                    ? aplicateDiscount(price)
                    : price.toFixed(2)
                }`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.firebase.auth,
});

export default connect(mapStateToProps, { authenticateUser, setUser })(IuguV2);
